import type { RoutePublic, Waypoint } from '@xingroute/schema'
import { waypointsEqual } from '../../lib/mergedWaypoints.js'

export type RightTab = 'intro' | 'history'

export type RouteDetailDraft = Partial<
  Pick<
    RoutePublic,
    | 'title'
    | 'summary'
    | 'status'
    | 'difficulty'
    | 'route_type'
    | 'activities'
    | 'region'
    | 'natural_features'
    | 'seasonality'
  >
>

export interface RouteDetailPageState {
  editing: boolean
  draft: RouteDetailDraft
  uploadingGpx: boolean
  mapWaypointState: {
    editing: boolean
    waypoints: Waypoint[]
  }
  focusWaypoint: { id: string; nonce: number } | null
  activeWaypoint: { id: string; nonce: number } | null
  pinnedWaypoints: string[]
  trackReversed: boolean
  focusMedia: { mediaId: string; nonce: number } | null
  showWaypointsOnMap: boolean
  rightTab: RightTab
}

export type RouteDetailPageAction =
  | { type: 'editing/set'; editing: boolean }
  | { type: 'draft/reset-from-route'; route: RoutePublic }
  | { type: 'draft/merge'; patch: RouteDetailDraft }
  | { type: 'uploading-gpx/set'; uploading: boolean }
  | { type: 'map-waypoints/set'; state: { editing: boolean; waypoints: Waypoint[] } }
  | { type: 'focus/waypoint'; id: string }
  | { type: 'focus/waypoint/clear' }
  | { type: 'active/waypoint'; id: string }
  | { type: 'active/waypoint/clear' }
  | { type: 'pinned-waypoints/add'; id: string }
  | { type: 'pinned-waypoints/toggle'; id: string }
  | { type: 'pinned-waypoints/clear' }
  | { type: 'track-reversed/toggle' }
  | { type: 'focus/media'; mediaId: string }
  | { type: 'show-waypoints/set'; show: boolean }
  | { type: 'right-tab/set'; tab: RightTab }

export const initialRouteDetailState: RouteDetailPageState = {
  editing: false,
  draft: {},
  uploadingGpx: false,
  mapWaypointState: { editing: false, waypoints: [] },
  focusWaypoint: null,
  activeWaypoint: null,
  pinnedWaypoints: [],
  trackReversed: false,
  focusMedia: null,
  showWaypointsOnMap: false,
  rightTab: 'intro',
}

export function routeDetailReducer(
  state: RouteDetailPageState,
  action: RouteDetailPageAction,
): RouteDetailPageState {
  switch (action.type) {
    case 'editing/set':
      return {
        ...state,
        editing: action.editing,
        ...(action.editing && state.trackReversed ? { trackReversed: false } : {}),
      }
    case 'draft/reset-from-route':
      return {
        ...state,
        draft: {
          title: action.route.title,
          summary: action.route.summary,
          status: action.route.status,
          difficulty: action.route.difficulty,
          route_type: action.route.route_type,
          activities: action.route.activities,
          region: action.route.region,
          natural_features: action.route.natural_features,
          seasonality: action.route.seasonality,
        },
      }
    case 'draft/merge':
      return { ...state, draft: { ...state.draft, ...action.patch } }
    case 'uploading-gpx/set':
      return { ...state, uploadingGpx: action.uploading }
    case 'map-waypoints/set':
      if (
        state.mapWaypointState.editing === action.state.editing &&
        waypointsEqual(state.mapWaypointState.waypoints, action.state.waypoints) &&
        !(action.state.editing && state.trackReversed)
      ) {
        return state
      }
      return {
        ...state,
        mapWaypointState: action.state,
        ...(action.state.editing && state.trackReversed ? { trackReversed: false } : {}),
      }
    case 'focus/waypoint':
      return {
        ...state,
        focusWaypoint: { id: action.id, nonce: (state.focusWaypoint?.nonce ?? 0) + 1 },
      }
    case 'focus/waypoint/clear':
      return state.focusWaypoint === null ? state : { ...state, focusWaypoint: null }
    case 'active/waypoint':
      return {
        ...state,
        activeWaypoint: { id: action.id, nonce: (state.activeWaypoint?.nonce ?? 0) + 1 },
      }
    case 'active/waypoint/clear':
      return state.activeWaypoint === null ? state : { ...state, activeWaypoint: null }
    case 'pinned-waypoints/add':
      return state.pinnedWaypoints.includes(action.id)
        ? state
        : { ...state, pinnedWaypoints: [...state.pinnedWaypoints, action.id] }
    case 'pinned-waypoints/toggle':
      return state.pinnedWaypoints.includes(action.id)
        ? { ...state, pinnedWaypoints: state.pinnedWaypoints.filter((id) => id !== action.id) }
        : { ...state, pinnedWaypoints: [...state.pinnedWaypoints, action.id] }
    case 'pinned-waypoints/clear':
      return state.pinnedWaypoints.length === 0 ? state : { ...state, pinnedWaypoints: [] }
    case 'track-reversed/toggle':
      return {
        ...state,
        trackReversed: !state.trackReversed,
        pinnedWaypoints: [],
      }
    case 'focus/media':
      return {
        ...state,
        focusMedia: { mediaId: action.mediaId, nonce: (state.focusMedia?.nonce ?? 0) + 1 },
      }
    case 'show-waypoints/set':
      if (action.show) return { ...state, showWaypointsOnMap: true }
      return {
        ...state,
        showWaypointsOnMap: false,
        pinnedWaypoints: [],
        activeWaypoint: null,
        focusWaypoint: null,
      }
    case 'right-tab/set':
      return { ...state, rightTab: action.tab }
  }
}
